import { parseError } from 'utils/utils';
import apiService from 'services/apiService';
import CachingEngine from 'utils/CachingEngine';

export interface HighScore {
  username?: string;
  score: number;
}

export interface GameScore {
  global_high_score: HighScore | null;
  user_high_score: HighScore | null;
  user_rank: number | null;
}

export interface Game {
  id: number;
  name: string;
  description: string;
  seed: string | null;
  global_high_score: HighScore | null;
  user_high_score: HighScore | null;
  user_rank: number | null;
}

export class GameService {
  private static instance: GameService;

  public static get(): GameService {
    if (!GameService.instance) {
      GameService.instance = new GameService();
    }

    return GameService.instance;
  }

  getGames = async (): Promise<Game[]> => {
    try {
      const cachedGames = await CachingEngine.getData('games');

      if (cachedGames) {
        return cachedGames;
      }

      const response = await apiService.api.get('/games');

      await CachingEngine.setData('games', response.data);
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  getGame = async (gameId: number): Promise<Game> => {
    try {
      const response = await apiService.api.get(`/games/${gameId}`);
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  setHighScore = async (
    gameId: number,
    userId: string,
    score: number,
  ): Promise<Game> => {
    try {
      const response = await apiService.api.post(
        `/games/${gameId}/scores/update/`,
        { score: score, user_id: userId },
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };

  setGlobalHighScore = async (gameId: number, score: number): Promise<Game> => {
    try {
      const response = await apiService.api.post(
        `/games/${gameId}/scores/new-highscore/`,
        { score: score },
      );
      return response.data;
    } catch (error) {
      console.error(error);
      return Promise.reject(new Error(parseError(error)));
    }
  };
}

const gameService = GameService.get();
export default gameService;
